import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Box,
  MenuItem,
  Typography,
  IconButton,
  LinearProgress,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import axios from "../../config/axiosConfig";
import { SUCURSALES_NOTICIA, esVideoNoticia, extraerYoutubeId } from "../../utils/noticiasHelpers";
import { comprimirImagen, formatearPeso } from "../../utils/comprimirImagen";

const MAX_FILE_MB = 10;
// Los videos (tutoriales cortos del sistema) se reproducen dentro de la noticia; mismo límite que el backend.
const MAX_VIDEO_MB = 200;

const RegisterNewsForm = ({ onSuccess, noticia = null }) => {
  const isEdit = Boolean(noticia?.id);

  const [form, setForm] = useState({
    titulo: noticia?.titulo || "",
    contenido: noticia?.contenido || "",
    sucursal: noticia?.sucursal || "Nacional",
    documento_carpeta_id: noticia?.documento_carpeta_id || "",
  });
  const [archivo, setArchivo] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [progreso, setProgreso] = useState(null);
  const [optimizando, setOptimizando] = useState(false);
  const [ahorro, setAhorro] = useState("");
  const [carpetasDocumentos, setCarpetasDocumentos] = useState([]);

  useEffect(() => {
    axios
      .get("/api/documentos/carpetas")
      .then(({ data }) => setCarpetasDocumentos(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Error al obtener carpetas de documentos:", err));
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFileChange = async (e) => {
    const elegido = e.target.files?.[0];
    e.target.value = "";
    if (!elegido) return;
    // Las fotos se achican antes de subir (una de celular pasa de varios MB a unos cientos de KB).
    let file = elegido;
    setAhorro("");
    if (elegido.type.startsWith("image/")) {
      setOptimizando(true);
      const r = await comprimirImagen(elegido);
      setOptimizando(false);
      file = r.file;
      if (r.reducida) setAhorro(`${formatearPeso(r.original)} → ${formatearPeso(r.final)} (optimizada)`);
    }
    const esVideo = esVideoNoticia(file.name);
    const maxMb = esVideo ? MAX_VIDEO_MB : MAX_FILE_MB;
    if (file.size > maxMb * 1024 * 1024) {
      setError(esVideo
        ? `El video no puede superar los ${MAX_VIDEO_MB} MB.`
        : `El archivo no puede superar los ${MAX_FILE_MB} MB (los videos, hasta ${MAX_VIDEO_MB} MB).`);
      return;
    }
    setError("");
    setArchivo(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("titulo", form.titulo);
      fd.append("contenido", form.contenido);
      fd.append("sucursal", form.sucursal);
      fd.append("documento_carpeta_id", form.documento_carpeta_id);
      if (archivo) fd.append("archivo", archivo);

      const config = archivo
        ? { onUploadProgress: (ev) => ev.total && setProgreso(Math.round((ev.loaded * 100) / ev.total)) }
        : undefined;
      if (isEdit) {
        await axios.put(`/api/news/${noticia.id}`, fd, config);
      } else {
        await axios.post("/api/news", fd, config);
      }

      onSuccess();
      if (!isEdit) {
        setForm({ titulo: "", contenido: "", sucursal: "Nacional", documento_carpeta_id: "" });
        setArchivo(null);
        setAhorro("");
      }
    } catch (err) {
      console.error("Error al guardar la noticia:", err);
      setError(err?.response?.data?.error || "No se pudo guardar la noticia.");
    } finally {
      setSaving(false);
      setProgreso(null);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="h6">{isEdit ? "Editar noticia" : "Registrar noticia"}</Typography>

      <TextField
        label="Título"
        name="titulo"
        value={form.titulo}
        onChange={handleChange}
        required
        fullWidth
      />
      <TextField
        label="Contenido"
        name="contenido"
        value={form.contenido}
        onChange={handleChange}
        required
        multiline
        minRows={4}
        fullWidth
        helperText={
          extraerYoutubeId(form.contenido)
            ? "Se mostrará el video de YouTube enlazado."
            : "Si pegas un enlace de YouTube o de Google Play, se mostrará el video o el botón de descarga."
        }
      />
      <TextField
        select
        label="Sucursal"
        name="sucursal"
        value={form.sucursal}
        onChange={handleChange}
        required
        fullWidth
      >
        {SUCURSALES_NOTICIA.map((s) => (
          <MenuItem key={s} value={s}>{s}</MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Carpeta de Documentos relacionada (opcional)"
        name="documento_carpeta_id"
        value={form.documento_carpeta_id}
        onChange={handleChange}
        fullWidth
      >
        <MenuItem value="">Ninguna</MenuItem>
        {carpetasDocumentos.map((c) => (
          <MenuItem key={c.id} value={c.id}>{c.nombre}</MenuItem>
        ))}
      </TextField>

      <Box>
        <Button component="label" variant="outlined" startIcon={<AttachFileIcon />} disabled={optimizando}>
          {optimizando ? "Optimizando imagen…" : archivo ? "Cambiar archivo" : "Adjuntar archivo"}
          <input type="file" hidden onChange={handleFileChange} accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx,.mp4,.webm,.mov" />
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          Imagen, PDF, Word o Excel hasta {MAX_FILE_MB} MB, o un video (MP4 recomendado) hasta {MAX_VIDEO_MB} MB.
        </Typography>
        {archivo && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                {archivo.name}
              </Typography>
              {ahorro && (
                <Typography variant="caption" color="success.main" sx={{ display: "block" }}>
                  {ahorro}
                </Typography>
              )}
            </Box>
            <IconButton size="small" onClick={() => { setArchivo(null); setAhorro(""); }} aria-label="Quitar archivo">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        {isEdit && noticia?.archivo_url && !archivo && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            Ya tiene un archivo adjunto. Sube uno nuevo para reemplazarlo.
          </Typography>
        )}
      </Box>

      {error && <Typography color="error" variant="body2">{error}</Typography>}

      {progreso !== null && (
        <Box>
          <LinearProgress variant="determinate" value={progreso} />
          <Typography variant="caption" color="text.secondary">
            {progreso < 100 ? `Subiendo archivo… ${progreso}%` : "Procesando…"}
          </Typography>
        </Box>
      )}

      <Button type="submit" variant="contained" color="primary" disabled={saving}>
        {saving ? "Guardando..." : isEdit ? "Guardar cambios" : "Registrar"}
      </Button>
    </Box>
  );
};

export default RegisterNewsForm;
